import { useCallback, useState } from 'react';
import { axios } from '../api/axios';

import { API_BASE_URL, environment, ERROR_MESSAGE } from '../constants';
import { ApplicationContainer } from '../providers';

const HOST = API_BASE_URL[environment as keyof typeof API_BASE_URL];

export const useNetwork = () => {
  const { setNotification } = ApplicationContainer.useContainer();

  const [data, setData] = useState<any>(null);
  const [responseData, setResponseData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<any>(null);

  const get = useCallback(
    async (url: string): Promise<any> => {
      setLoading(true);
      try {
        const response = await axios.get(HOST + url);
        const apiData = response.data;

        setStatus(response.status >= 200 && response.status < 300);
        setResponseData(apiData);
        let apiPayload = apiData.data ?? apiData;
        if (/chart|report-templates|documents|promo/gi.test(url)) {
          apiPayload = apiData;
        }

        setData(apiPayload);
        return apiPayload;
      } catch (err: any) {
        setError(err);
        setLoading(false);
        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setNotification]
  );

  const post = useCallback(
    async (url: string, requestJSON: any) => {
      setLoading(true);
      try {
        const response = await axios.post(HOST + url, requestJSON);
        const apiData = response.data;
        setStatus(response.status >= 200 && response.status < 300);
        setResponseData(apiData);

        if (/4|5/gi.test(response.status.toString())) {
          setError('error');
          return 'error';
        }

        if (/chart|documents|copyTemplate/gi.test(url)) {
          setData(apiData);
          return apiData;
        }
        const apiResponse = apiData.data ?? apiData;
        setData(apiResponse);
        return apiResponse;
      } catch (err: any) {
        setError(err);
        setLoading(false);

        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setNotification]
  );

  const formData = useCallback(
    async (url: string, requestJSON: any) => {
      setLoading(true);
      try {
        const response = await axios.post(HOST + url, requestJSON, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        const apiData = response.data;
        setStatus(response.status >= 200 && response.status < 300);
        setData(apiData);
        setLoading(false);
        return apiData;
      } catch (err: any) {
        setError(err);
        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setNotification]
  );

  const put = useCallback(
    async (url: string, requestJSON?: any) => {
      try {
        const response = await axios.put(HOST + url, requestJSON);
        setStatus(response.status >= 200 && response.status < 300);
        const apiData = response.data;
        setData(apiData.data);
        return apiData.data;
      } catch (err: any) {
        setError(err);
        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setNotification]
  );

  const remove = useCallback(
    async (url: string, requestJSON?: any) => {
      try {
        const response = await axios.delete(HOST + url, {
          data: requestJSON,
        });
        setStatus(response.status >= 200 && response.status < 300);
        const apiData = response.data;
        setData(apiData.data);
        return apiData.data;
      } catch (err: any) {
        setError(err);
        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setNotification]
  );

  const patch = useCallback(
    async (url: string, requestJSON?: any) => {
      try {
        const response = await axios.patch(HOST + url, requestJSON);
        setStatus(response.status >= 200 && response.status < 300);
        const apiData = response.data;
        if (
          /chart|report-templates|sub-accounts|update-activation/gi.test(url)
        ) {
          setData(apiData);
          return apiData;
        }
        setResponseData(apiData);
        setData(apiData.data);
        return apiData.data;
      } catch (err: any) {
        setError(err);
        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setNotification]
  );

  return {
    get,
    post,
    formData,
    put,
    data,
    status,
    error,
    loading,
    setLoading,
    remove,
    patch,
    responseData,
  };
};
